import { RefreshTokenResult } from "@kinde/js-utils";
import { IAuthenticatedUser, NotificationType, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { KINDE_CLIENT_ID, KINDE_DOMAIN } from "@next-platform/client-common-core/infra/constants";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { AxiosError } from "axios";
import { useCallback, useState } from "react";

import { getOfflineNotificationMessage } from "@/components/common/Notification/utils/notification";
import useEnsureUserProfile from "@/features/user/utils/useEnsureUserProfile";
import useToast from "@/hooks/useToast";
import jsonApiService from "@/services/jsonApiService";
import useAuth from "@/services/useAuth";
import { ServerErrorResponse } from "@/types/api";

const logger = appLogger.withTag("useApi");

const errorTemplate = {
  errorCode: "",
  error: "",
  message: "",
  status: 0,
};

//This T parameter is to describe the return type of callApi function.
//Mandatory to pass if you are going to use it.
function useApi<T>() {
  const [isLoading, setIsLoading] = useState(false);
  const [errorData, setErrorData] = useState<ServerErrorResponse>(errorTemplate);
  const [data, setData] = useState<T | null>(null);
  const { isLoading: isAuthLoading, getToken, kindeGetPermissions, kindeGetClaim, kindeRefreshToken, user } = useAuth() || {};
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const { setPermission, setRoles, guestUserId } = useUserInfo();
  const { setAuthenticatedUser } = useEnsureUserProfile();

  const resetError = () => {
    setErrorData({ ...errorTemplate });
  };

  const callApi = useCallback(
    async <Args extends unknown[]>(apiFunc: (...args: Args) => Promise<T>, ...args: Args): Promise<T | undefined> => {
      if (!isAuthLoading) {
        if (!args.includes("queue") && !navigator.onLine) {
          displayToast({ message: getOfflineNotificationMessage(t), type: NotificationType.warning });
          throw new Error("No connection. Please check and retry");
        }

        setIsLoading(true);
        setErrorData({ ...errorTemplate });

        // Awaited for its side effect, not its value: on the partner path
        // `getToken()` runs a pre-request refresh, and the request bearer is
        // applied by the shared axios interceptor via `authProvider.getToken()`
        // (which only reads storage), not stored on the service instance here.
        try {
          await getToken();
        } catch (error) {
          logger.error("Failed to get token", error);
        }
        if (guestUserId) {
          jsonApiService.setGuestUserId(guestUserId);
        }

        try {
          const boundApiFunc = apiFunc.bind(jsonApiService);
          const result = await boundApiFunc(...args);

          setData(result);
          setIsLoading(false);
          return result;
        } catch (error) {
          setIsLoading(false);

          if (error instanceof AxiosError) {
            const axiosError = error as AxiosError<ServerErrorResponse>;
            if (axiosError.response?.data?.error) {
              setErrorData(axiosError.response.data);
              throw new Error(axiosError.response.data.error);
            }
          }

          if (error instanceof Error) {
            setErrorData({ ...errorTemplate, message: error.message });
            throw new Error(error.message);
          }

          setErrorData({ ...errorTemplate, error: "An unknown error occurred" });
          throw new Error("An unknown error occurred");
        }
      }
    },
    [displayToast, getToken, guestUserId, isAuthLoading, t],
  );

  const forceRefreshToken = useCallback(() => {
    kindeRefreshToken({
      clientId: KINDE_CLIENT_ID as string,
      domain: KINDE_DOMAIN as string,
    })
      .then(async (userToken: RefreshTokenResult) => {
        if (userToken.success) {
          // we need to update the useAuth permissions with Kinde permissions because the permissions that we use across application are extracted from this Auth Provider permissions which is maintained in useAuth.

          const [permissions, roles] = await Promise.allSettled([kindeGetPermissions(), kindeGetClaim("roles")]);
          if (permissions.status === "fulfilled") {
            setPermission(permissions.value.permissions);
          }
          if (roles.status === "fulfilled") {
            setRoles(roles?.value?.value ?? []);
          }
          const newUser = { ...user } as IAuthenticatedUser;
          setAuthenticatedUser(newUser);
        } else throw null;
      })
      .catch(() => {
        setErrorData({ ...errorTemplate, error: "Looks like there's a networking issue. Try reloading the page" });
        throw new Error("Looks like there's a networking issue. Try reloading the page");
      });
  }, [kindeGetClaim, kindeGetPermissions, kindeRefreshToken, setAuthenticatedUser, setPermission, setRoles, user]);
  return {
    data,
    jsonApiService,
    callApi,
    forceRefreshToken,
    isLoading,
    errorData,
    resetError,
  };
}

export default useApi;
